"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.OFFLINE_ENVELOPE_CANONICAL_KEYS = exports.OfflineEnvelopeError = exports.OFFLINE_EVENT_SCHEMA_VERSION = void 0;
exports.parseOfflineEnvelope = parseOfflineEnvelope;
exports.serializeOfflineEnvelope = serializeOfflineEnvelope;
exports.classifySync = classifySync;
// packages/contracts/src/offline-event.ts — Fase 01, Plano 08 (NFR-05/D-07).
//
// Contrato persistente do envelope offline. schemaVersion 1 é a opção
// device-sequence-vector (ADR-006). Campos aditivos são permitidos; breaking
// change exige nova schemaVersion e migração.
exports.OFFLINE_EVENT_SCHEMA_VERSION = 1;
const ENVELOPE_KEYS = [
    'aggregateId',
    'baseVersion',
    'conflictVector',
    'deviceId',
    'deviceSequence',
    'deviceTimestamp',
    'idempotencyId',
    'payload',
    'schemaVersion',
];
class OfflineEnvelopeError extends Error {
    reasonCode;
    constructor(reasonCode, message) {
        super(message);
        this.name = 'OfflineEnvelopeError';
        this.reasonCode = reasonCode;
    }
}
exports.OfflineEnvelopeError = OfflineEnvelopeError;
function isRecord(value) {
    return value !== null && typeof value === 'object' && !Array.isArray(value);
}
function requireNonEmptyString(obj, key) {
    const v = obj[key];
    if (typeof v !== 'string' || v.length === 0) {
        throw new OfflineEnvelopeError('invalid-field', `campo ${key} obrigatório (string não vazia)`);
    }
    return v;
}
function requireInt(obj, key, min) {
    const v = obj[key];
    if (typeof v !== 'number' || !Number.isInteger(v) || v < min) {
        throw new OfflineEnvelopeError('invalid-field', `campo ${key} deve ser inteiro >= ${min}`);
    }
    return v;
}
function parseConflictVector(value) {
    if (!isRecord(value)) {
        throw new OfflineEnvelopeError('invalid-field', 'conflictVector deve ser objeto');
    }
    const out = {};
    for (const [k, v] of Object.entries(value)) {
        if (typeof v !== 'number' || !Number.isInteger(v) || v < 0) {
            throw new OfflineEnvelopeError('invalid-field', `conflictVector[${k}] deve ser inteiro >= 0`);
        }
        out[k] = v;
    }
    return out;
}
/**
 * parseOfflineEnvelope — valida o envelope persistente. schemaVersion diferente
 * de 1 é rejeitado (não há migração automática na v1). Campos aditivos além
 * dos conhecidos são ignorados na estrutura tipada e preservados só se o
 * caller guardar o input original; o parse canônico materializa os campos
 * do contrato.
 */
function parseOfflineEnvelope(input) {
    if (!isRecord(input)) {
        throw new OfflineEnvelopeError('invalid-envelope', 'envelope deve ser objeto');
    }
    const schemaVersion = requireInt(input, 'schemaVersion', 0);
    if (schemaVersion !== exports.OFFLINE_EVENT_SCHEMA_VERSION) {
        throw new OfflineEnvelopeError('schema-version', `schemaVersion ${schemaVersion} rejeitada (esperado ${exports.OFFLINE_EVENT_SCHEMA_VERSION})`);
    }
    const deviceTimestamp = requireNonEmptyString(input, 'deviceTimestamp');
    if (!/^\d{4}-\d{2}-\d{2}T/.test(deviceTimestamp)) {
        throw new OfflineEnvelopeError('invalid-field', 'deviceTimestamp deve ser ISO-8601');
    }
    return {
        idempotencyId: requireNonEmptyString(input, 'idempotencyId'),
        deviceSequence: requireInt(input, 'deviceSequence', 1),
        schemaVersion,
        deviceTimestamp,
        deviceId: requireNonEmptyString(input, 'deviceId'),
        aggregateId: requireNonEmptyString(input, 'aggregateId'),
        baseVersion: requireInt(input, 'baseVersion', 0),
        conflictVector: parseConflictVector(input.conflictVector),
        payload: 'payload' in input ? input.payload : undefined,
    };
}
function sortVector(vector) {
    const keys = Object.keys(vector).sort();
    const out = {};
    for (const k of keys) {
        const n = vector[k];
        if (n === undefined)
            continue;
        out[k] = n;
    }
    return out;
}
/** Serialização canônica: JSON UTF-8, chaves do envelope em ordem lexicográfica. */
function serializeOfflineEnvelope(envelope) {
    const canonical = {
        aggregateId: envelope.aggregateId,
        baseVersion: envelope.baseVersion,
        conflictVector: sortVector(envelope.conflictVector),
        deviceId: envelope.deviceId,
        deviceSequence: envelope.deviceSequence,
        deviceTimestamp: envelope.deviceTimestamp,
        idempotencyId: envelope.idempotencyId,
        payload: envelope.payload,
        schemaVersion: envelope.schemaVersion,
    };
    return JSON.stringify(canonical);
}
function vectorsDiverge(existing, incoming) {
    const ids = new Set([...Object.keys(existing), ...Object.keys(incoming)]);
    for (const id of ids) {
        const a = existing[id] ?? 0;
        const b = incoming[id] ?? 0;
        if (a !== b)
            return true;
    }
    return false;
}
/**
 * classifySync — decisão de inbox. Nunca last-write-wins. Gap de sequência
 * não é motivo de rejeição (telemetria). Mesmo idempotencyId = duplicate.
 */
function classifySync(existing, incoming, serverVersion) {
    if (incoming.schemaVersion !== exports.OFFLINE_EVENT_SCHEMA_VERSION) {
        return { status: 'rejected', reasonCode: 'schema-version' };
    }
    if (existing && existing.idempotencyId === incoming.idempotencyId) {
        return { status: 'duplicate', serverVersion };
    }
    if (existing && existing.aggregateId === incoming.aggregateId) {
        if (existing.baseVersion !== incoming.baseVersion) {
            return { status: 'conflict', serverVersion, conflictCode: 'base-version' };
        }
        if (vectorsDiverge(existing.conflictVector, incoming.conflictVector)) {
            return { status: 'conflict', serverVersion, conflictCode: 'conflict-vector' };
        }
    }
    return { status: 'accepted', serverVersion };
}
exports.OFFLINE_ENVELOPE_CANONICAL_KEYS = ENVELOPE_KEYS;
//# sourceMappingURL=offline-event.js.map